import { Check, Info, MapPin, Search, X } from 'lucide-react';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/expenses/capture/NativeSelect';
import { cn } from '@/lib/utils';
import { countryOptions } from '@/profile/countries';
import {
  addressFormat,
  editAddressPart,
  missingParts,
  type AddressPart,
  type AddressValue,
} from './address-value';
import { GoogleAttribution } from './google-attribution';
import { MAX_INPUT, useAddressSearch, type AddressSuggestion } from './use-address-search';

export interface AddressFieldProps {
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  /** The search box's label; «Indirizzo» by default («Sede legale», «Sede»…). */
  label?: string;
  /** Beside the label, in the body weight: «· facoltativa». */
  labelNote?: string;
  disabled?: boolean;
}

/** A field filled from the chosen place (board 2.2/2.4): the selection tint over the card. */
const FROM_PLACE =
  'bg-[color-mix(in_oklab,var(--blue-tint),var(--surface)_55%)] dark:bg-[color-mix(in_oklab,var(--blue-tint),var(--surface)_55%)]';

/** 44 px targets on a phone (the shell's narrow container), the app's 38 px elsewhere. */
const TOUCH = '@max-[619px]/shell:min-h-11';

type Translate = ReturnType<typeof useTranslation<'common'>>['t'];

/** The main text with the typed parts in bold, from the service's `mainTextMatches`. */
function highlighted(suggestion: AddressSuggestion): ReactNode[] {
  const { mainText, mainTextMatches } = suggestion;
  const nodes: ReactNode[] = [];
  let at = 0;
  for (const { start, end } of [...mainTextMatches].sort((a, b) => a.start - b.start)) {
    if (start < at || end <= start || end > mainText.length) {
      continue;
    }
    if (start > at) {
      nodes.push(mainText.slice(at, start));
    }
    nodes.push(
      <mark key={start} className="bg-transparent font-extrabold text-inherit">
        {mainText.slice(start, end)}
      </mark>,
    );
    at = end;
  }
  if (at < mainText.length) {
    nodes.push(mainText.slice(at));
  }
  return nodes;
}

function partLabel(part: AddressPart, countryCode: string, t: Translate): string {
  const italian = countryCode === 'IT';
  switch (part) {
    case 'street':
      return t('address.parts.street');
    case 'streetNumber':
      return t('address.parts.streetNumber');
    case 'postalCode':
      return italian ? t('address.parts.postalCode') : t('address.parts.postalCodeAbroad');
    case 'city':
      return t('address.parts.city');
    case 'province':
      return t(`address.parts.${addressFormat(countryCode).region}`);
    case 'countryCode':
      return t('address.parts.countryCode');
  }
}

interface Cell {
  readonly part: AddressPart;
  /** Columns on a narrow card (2) and a wide one (3). */
  readonly narrow: 1 | 2;
  readonly wide: 1 | 2;
}

/** The fields in the country's order (board 2.3), Italy's by default (2.2). */
function layout(countryCode: string): Cell[] {
  const format = addressFormat(countryCode);
  const street: Cell[] = format.numberFirst
    ? [
        { part: 'streetNumber', narrow: 1, wide: 1 },
        { part: 'street', narrow: 1, wide: 2 },
      ]
    : [
        { part: 'street', narrow: 2, wide: 2 },
        { part: 'streetNumber', narrow: 1, wide: 1 },
      ];
  const postal: Cell = { part: 'postalCode', narrow: 1, wide: 1 };
  const city: Cell = { part: 'city', narrow: 1, wide: 1 };
  const place = format.postalCodeAfterCity ? [city, postal] : [postal, city];
  const country: Cell = { part: 'countryCode', narrow: format.numberFirst ? 1 : 2, wide: 1 };
  return [...street, ...place, { part: 'province', narrow: 1, wide: 1 }, country];
}

const AUTOCOMPLETE: Record<AddressPart, string> = {
  street: 'address-line1',
  streetNumber: 'off',
  postalCode: 'postal-code',
  city: 'address-level2',
  province: 'address-level1',
  countryCode: 'country',
};

/**
 * The console's one address component (#958, report §3.11, boards 2.1–2.6). The person types and
 * the address service proposes addresses with Google's attribution; picking one fills street,
 * number, postal code, city, province, country and coordinates, and every field stays editable.
 * When the service cannot answer (no key yet, budget, Google down) the fields are typed by hand.
 * Nothing here blocks saving: the host form decides what it needs.
 *
 * It takes and returns an {@link AddressValue}; it does not know who the address belongs to, and
 * only what is typed in the search box leaves the console.
 */
export function AddressField({
  value,
  onChange,
  label,
  labelNote,
  disabled = false,
}: AddressFieldProps) {
  const { t, i18n } = useTranslation('common');
  const language = i18n.language.startsWith('en') ? 'en' : 'it';
  const id = useId();
  const search = useAddressSearch(language);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [filling, setFilling] = useState(false);
  const [placeGone, setPlaceGone] = useState(false);
  // The place the fields were filled from: what «changing a part the place had» compares with.
  const [place, setPlace] = useState<AddressValue | null>(value.placeId !== null ? value : null);
  // A saved address that arrives after mount (a form reset on opening, a page that loaded) is the
  // place too: its parts are Google's until the person changes them. Only a value from outside
  // brings a place id the field has not seen: a pick sets the place with the value.
  if (value.placeId !== null && place?.placeId !== value.placeId) {
    setPlace(value);
  }
  // Which pick the last place call belongs to, and whether the field is still there to fill.
  const picking = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const countries = useMemo(() => countryOptions(language), [language]);

  const listId = `${id}-suggestions`;
  const hintId = `${id}-hint`;
  const optionId = (index: number) => `${id}-option-${index}`;
  const listShown =
    open && !disabled && (search.status === 'searching' || search.status === 'ready');
  const options = search.status === 'ready' ? search.suggestions : [];
  const filled = value.placeId !== null && place?.placeId === value.placeId;
  const missing = filled ? missingParts(value) : [];

  const type = (text: string) => {
    picking.current += 1;
    setFilling(false);
    setQuery(text);
    setPlaceGone(false);
    setActive(-1);
    setOpen(true);
    search.search(text);
  };

  const pick = async (suggestion: AddressSuggestion) => {
    const attempt = ++picking.current;
    setOpen(false);
    setActive(-1);
    setQuery(suggestion.text);
    setFilling(true);
    const outcome = await search.choose(suggestion);
    // A late answer is dropped once the person typed again, picked another, or left the form.
    if (attempt !== picking.current || !mounted.current) {
      return;
    }
    setFilling(false);
    if (outcome.status === 'ok') {
      setPlace(outcome.address);
      onChange(outcome.address);
    } else if (outcome.status === 'not-found') {
      setPlaceGone(true);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (options.length === 0) {
        return;
      }
      event.preventDefault();
      setOpen(true);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) =>
        step === 1
          ? (current + 1) % options.length
          : current <= 0
            ? options.length - 1
            : current - 1,
      );
      return;
    }
    if (event.key === 'Enter' && listShown) {
      // Enter in the list never submits the host form.
      event.preventDefault();
      const chosen = options[active];
      if (chosen !== undefined) {
        void pick(chosen);
      }
      return;
    }
    if (event.key === 'Escape' && listShown) {
      // Board 2.1: Esc closes the list and leaves the text; a dialog around stays open.
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      setActive(-1);
      search.close();
    }
  };

  const clear = () => {
    picking.current += 1;
    setFilling(false);
    setQuery('');
    setOpen(false);
    setActive(-1);
    setPlaceGone(false);
    search.close();
  };

  /** Board 2.4: a part as Google gave it is tinted; an empty or retyped one is a plain field. */
  const fromPlace = (part: AddressPart) =>
    filled && place !== null && value[part].trim() !== '' && value[part] === place[part];

  const edit = (part: AddressPart, text: string) =>
    onChange(editAddressPart(value, part, text, place));

  const missingNames = new Intl.ListFormat(language, { type: 'conjunction' }).format(
    missing.map((part) => t(`address.missingPart.${part}`)),
  );
  const activeOption = listShown && options[active] !== undefined ? optionId(active) : undefined;

  return (
    <div className="@container flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${id}-search`} className="text-sm font-semibold text-foreground">
          {label ?? t('address.label')}
          {labelNote !== undefined && (
            <>
              {' '}
              <span className="font-normal text-muted-foreground">{labelNote}</span>
            </>
          )}
        </label>
        {/* Board 2.5: the list opens under the hint, over the fields below. */}
        <div className="relative flex flex-col gap-1.5">
          <div className="relative">
            <span
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-caption"
              aria-hidden
            >
              {filled ? <MapPin size={16} /> : <Search size={16} />}
            </span>
            <Input
              id={`${id}-search`}
              role="combobox"
              aria-expanded={listShown}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={activeOption}
              aria-describedby={hintId}
              autoComplete="off"
              spellCheck={false}
              maxLength={MAX_INPUT}
              placeholder={t('address.searchPlaceholder')}
              value={query}
              disabled={disabled}
              onChange={(event) => type(event.target.value)}
              onKeyDown={onKeyDown}
              onBlur={() => {
                setOpen(false);
                setActive(-1);
              }}
              className={cn('pr-11 pl-9', TOUCH)}
            />
            {query !== '' && !disabled && (
              <button
                type="button"
                onClick={clear}
                aria-label={t('address.clear')}
                className={cn(
                  'absolute top-1/2 right-0.5 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-md text-caption hover:text-foreground',
                  'focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:outline-none',
                  '@max-[619px]/shell:size-11',
                )}
              >
                <X size={16} aria-hidden />
              </button>
            )}
          </div>
          <small id={hintId} className="text-xs leading-snug text-caption">
            {t('address.searchHint')}
          </small>
          {listShown && (
            <div className="absolute top-full right-0 left-0 z-30 mt-1 overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-[var(--shadow-2)]">
              {search.status === 'searching' && (
                <p className="px-3 py-3 text-sm text-muted-foreground">{t('address.searching')}</p>
              )}
              {search.status === 'ready' && options.length === 0 && (
                <p className="px-3 py-3 text-sm text-muted-foreground">{t('address.noResults')}</p>
              )}
              {/* Only the options inside the listbox; the states and the attribution sit beside it. */}
              <div
                id={listId}
                role="listbox"
                aria-label={t('address.suggestionsLabel')}
                hidden={options.length === 0}
                className="max-h-[min(22rem,55vh)] overflow-y-auto py-1"
              >
                {options.map((suggestion, index) => (
                  <div
                    key={suggestion.placeId}
                    id={optionId(index)}
                    role="option"
                    aria-selected={index === active}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => void pick(suggestion)}
                    className={cn(
                      'flex min-h-12 cursor-pointer items-center gap-2.5 px-3 py-1.5',
                      index === active && 'bg-selected text-selected-foreground',
                    )}
                  >
                    <MapPin size={17} className="shrink-0 text-caption" aria-hidden />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-semibold">
                        {highlighted(suggestion)}
                      </span>
                      <small className="truncate text-xs text-caption">
                        {suggestion.secondaryText}
                      </small>
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 border-t border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
                <span>{t('address.typeBelow')}</span>
                <GoogleAttribution />
              </div>
            </div>
          )}
        </div>
        <span className="sr-only" aria-live="polite">
          {filling
            ? t('address.filling')
            : search.status === 'ready' && open
              ? t('address.suggestionsCount', { count: options.length })
              : ''}
        </span>
      </div>

      {search.status === 'unavailable' && (
        <div
          role="status"
          className="flex items-start gap-2.5 rounded-md border border-border bg-muted px-3 py-2.5 text-sm text-foreground"
        >
          <Info size={18} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
          <p>
            <b>{t('address.unavailableTitle')}</b> {t('address.unavailableBody')}
          </p>
        </div>
      )}
      {placeGone && (
        <div
          role="status"
          className="flex items-start gap-2.5 rounded-md border border-border bg-muted px-3 py-2.5 text-sm text-foreground"
        >
          <Info size={18} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
          <p>{t('address.placeGone')}</p>
        </div>
      )}

      <fieldset
        disabled={disabled}
        aria-busy={filling}
        className="flex flex-col gap-2.5 rounded-md border border-border bg-background px-3.5 py-3"
      >
        {/* Named after the field, so two addresses in one form (an office, a site) are told apart. */}
        <legend className="sr-only">
          {label === undefined
            ? t('address.partsLegend')
            : `${label} · ${t('address.partsLegend')}`}
        </legend>
        {filling && <p className="text-xs text-muted-foreground">{t('address.filling')}</p>}
        {filled && !filling && (
          <p className="flex items-center gap-1.5 text-xs font-semibold text-success">
            <Check size={13} aria-hidden />
            {t('address.filled')}
          </p>
        )}
        <div className="grid grid-cols-2 items-end gap-3 @min-[520px]:grid-cols-[2fr_1fr_1fr]">
          {layout(value.countryCode).map(({ part, narrow, wide }) => {
            const fieldId = `${id}-${part}`;
            const optional = part === 'province' && value.countryCode !== 'IT';
            return (
              <div
                key={part}
                className={cn(
                  'flex min-w-0 flex-col gap-1.5',
                  narrow === 2 ? 'col-span-2' : 'col-span-1',
                  wide === 2 ? '@min-[520px]:col-span-2' : '@min-[520px]:col-span-1',
                )}
              >
                <label htmlFor={fieldId} className="text-sm font-semibold text-foreground">
                  {partLabel(part, value.countryCode, t)}
                  {optional && (
                    <span className="font-normal text-caption"> · {t('address.ifUsed')}</span>
                  )}
                </label>
                {part === 'countryCode' ? (
                  <NativeSelect
                    id={fieldId}
                    autoComplete={AUTOCOMPLETE[part]}
                    value={value.countryCode}
                    data-from-place={fromPlace(part) ? '' : undefined}
                    onChange={(event) => edit(part, event.target.value)}
                    className={cn(TOUCH, fromPlace(part) && FROM_PLACE)}
                  >
                    <option value="">{t('address.chooseCountry')}</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </NativeSelect>
                ) : (
                  <Input
                    id={fieldId}
                    autoComplete={AUTOCOMPLETE[part]}
                    autoCapitalize={
                      part === 'province' && value.countryCode === 'IT' ? 'characters' : undefined
                    }
                    inputMode={
                      part === 'postalCode' && value.countryCode === 'IT' ? 'numeric' : undefined
                    }
                    value={value[part]}
                    placeholder="—"
                    data-from-place={fromPlace(part) ? '' : undefined}
                    onChange={(event) => edit(part, event.target.value)}
                    className={cn(TOUCH, fromPlace(part) && FROM_PLACE)}
                  />
                )}
              </div>
            );
          })}
        </div>
        {missing.length > 0 && !filling && (
          <p className="text-xs font-semibold text-warning">
            {t('address.missing', { count: missing.length, parts: missingNames })}
          </p>
        )}
      </fieldset>
    </div>
  );
}
